/**
 * Settlement data generator
 * Generates realistic settlements linked to merchants, wallets, and transactions
 */

import { faker } from '@faker-js/faker'
import type {
  Settlement,
  SettlementListItem,
  SettlementStatus,
  SettlementCycle,
  SettlementBankAccount,
  SettlementFees,
  SettlementTimelineEvent,
  SettlementTransactionRef
} from '../../shared/types/settlements'
import type { CurrencyCode } from '../../shared/types/common'

// Configuration for settlement generation
const SETTLEMENT_CONFIG = {
  TOTAL_COUNT: 100,
  SEED: 12345,

  // Status distribution
  STATUS_WEIGHTS: {
    completed: 0.60,
    pending: 0.20,
    processing: 0.12,
    failed: 0.05,
    cancelled: 0.03
  } as Record<SettlementStatus, number>,

  // Settlement cycles distribution
  CYCLE_WEIGHTS: {
    'T+2': 0.25,
    'T+7': 0.35,
    'T+14': 0.25,
    'T+30': 0.15
  } as Record<SettlementCycle, number>,

  // Fee rates (percentage of gross)
  FEE_RATES: {
    processing: { min: 0.015, max: 0.025 }, // 1.5-2.5%
    platform: { min: 0.005, max: 0.010 }, // 0.5-1%
    taxWithholding: { min: 0, max: 0.005 } // 0-0.5%
  },

  // Amount ranges per transaction (for settlement totals)
  AMOUNT_RANGES: {
    minTransactions: 5,
    maxTransactions: 50,
    minAmount: 50,
    maxAmount: 2500
  }
}

// Bank names for settlements
const BANK_NAMES = [
  'Banco do Brasil',
  'Itaú Unibanco',
  'Bradesco',
  'Santander Brasil',
  'Caixa Econômica',
  'Nubank',
  'BTG Pactual',
  'Inter',
  'C6 Bank',
  'Safra'
]

/**
 * Generate a weighted random status
 */
function generateStatus(): SettlementStatus {
  const rand = faker.number.float({ min: 0, max: 1 })
  let cumulative = 0

  for (const [status, weight] of Object.entries(SETTLEMENT_CONFIG.STATUS_WEIGHTS)) {
    cumulative += weight
    if (rand <= cumulative) {
      return status as SettlementStatus
    }
  }

  return 'completed'
}

/**
 * Generate a weighted random settlement cycle
 */
function generateCycle(): SettlementCycle {
  const rand = faker.number.float({ min: 0, max: 1 })
  let cumulative = 0

  for (const [cycle, weight] of Object.entries(SETTLEMENT_CONFIG.CYCLE_WEIGHTS)) {
    cumulative += weight
    if (rand <= cumulative) {
      return cycle as SettlementCycle
    }
  }

  return 'T+7'
}

/**
 * Get days for settlement cycle
 */
function getCycleDays(cycle: SettlementCycle): number {
  switch (cycle) {
    case 'T+2': return 2
    case 'T+7': return 7
    case 'T+14': return 14
    case 'T+30': return 30
    default: return 7
  }
}

/**
 * Generate bank account details
 */
function generateBankAccount(): SettlementBankAccount {
  return {
    bankName: faker.helpers.arrayElement(BANK_NAMES),
    bankCode: faker.string.numeric(3),
    accountNumber: faker.finance.accountNumber(10),
    accountType: faker.helpers.arrayElement(['checking', 'savings']),
    routingNumber: faker.string.numeric(9),
    beneficiaryName: faker.company.name()
  }
}

/**
 * Generate fee breakdown
 */
function generateFees(grossAmount: number): SettlementFees {
  const processingRate = faker.number.float(SETTLEMENT_CONFIG.FEE_RATES.processing)
  const platformRate = faker.number.float(SETTLEMENT_CONFIG.FEE_RATES.platform)
  const taxRate = faker.number.float(SETTLEMENT_CONFIG.FEE_RATES.taxWithholding)

  const processingFee = Number((grossAmount * processingRate).toFixed(2))
  const platformFee = Number((grossAmount * platformRate).toFixed(2))
  const taxWithholding = Number((grossAmount * taxRate).toFixed(2))
  const otherFees = Number((faker.number.float({ min: 0, max: grossAmount * 0.002 })).toFixed(2))

  return {
    processingFee,
    platformFee,
    taxWithholding,
    otherFees,
    totalFees: Number((processingFee + platformFee + taxWithholding + otherFees).toFixed(2))
  }
}

/**
 * Generate timeline events based on status
 */
function generateTimeline(
  status: SettlementStatus,
  createdAt: Date,
  completedAt?: Date
): SettlementTimelineEvent[] {
  const timeline: SettlementTimelineEvent[] = []

  // Created event
  timeline.push({
    event: 'created',
    timestamp: createdAt.toISOString(),
    description: 'Settlement created and queued'
  })

  if (status === 'pending') {
    return timeline
  }

  // Processing event
  const processingTime = new Date(createdAt.getTime() + faker.number.int({ min: 1, max: 24 }) * 60 * 60 * 1000)
  timeline.push({
    event: 'processing',
    timestamp: processingTime.toISOString(),
    description: 'Settlement processing started'
  })

  if (status === 'processing') {
    return timeline
  }

  // Sent event (for completed/failed)
  if (status === 'completed' || status === 'failed') {
    const sentTime = new Date(processingTime.getTime() + faker.number.int({ min: 1, max: 12 }) * 60 * 60 * 1000)
    timeline.push({
      event: 'sent',
      timestamp: sentTime.toISOString(),
      description: 'Settlement sent to bank'
    })

    // Final event
    if (status === 'completed' && completedAt) {
      timeline.push({
        event: 'completed',
        timestamp: completedAt.toISOString(),
        description: 'Settlement completed successfully'
      })
    } else if (status === 'failed') {
      timeline.push({
        event: 'failed',
        timestamp: new Date(sentTime.getTime() + faker.number.int({ min: 1, max: 6 }) * 60 * 60 * 1000).toISOString(),
        description: faker.helpers.arrayElement([
          'Bank account validation failed',
          'Insufficient funds',
          'Bank transfer rejected',
          'Invalid routing number'
        ])
      })
    }
  }

  if (status === 'cancelled') {
    timeline.push({
      event: 'cancelled',
      timestamp: new Date(createdAt.getTime() + faker.number.int({ min: 1, max: 48 }) * 60 * 60 * 1000).toISOString(),
      description: 'Settlement cancelled by merchant'
    })
  }

  return timeline
}

/**
 * Generate a single settlement
 */
function generateSettlement(
  merchantId: string,
  walletId: string,
  currency: CurrencyCode,
  transactionIds: string[],
  _index: number
): Settlement {
  const status = generateStatus()
  const cycle = generateCycle()
  const cycleDays = getCycleDays(cycle)

  // Generate dates
  const now = new Date()
  const daysAgo = faker.number.int({ min: 1, max: 90 })
  const periodEnd = new Date(now.getTime() - daysAgo * 24 * 60 * 60 * 1000)
  const periodStart = new Date(periodEnd.getTime() - 7 * 24 * 60 * 60 * 1000) // 7 day period
  const settlementDate = new Date(periodEnd.getTime() + cycleDays * 24 * 60 * 60 * 1000)
  const expectedDate = settlementDate
  const createdAt = periodEnd

  // Completed date based on status
  let completedAt: Date | undefined
  if (status === 'completed') {
    completedAt = new Date(settlementDate.getTime() + faker.number.int({ min: 0, max: 2 }) * 24 * 60 * 60 * 1000)
  }

  // Generate amounts
  const transactionCount = transactionIds.length
  const grossAmount = Number((
    transactionCount
    * faker.number.float({
      min: SETTLEMENT_CONFIG.AMOUNT_RANGES.minAmount,
      max: SETTLEMENT_CONFIG.AMOUNT_RANGES.maxAmount
    })
  ).toFixed(2))

  const fees = generateFees(grossAmount)
  const netAmount = Number((grossAmount - fees.totalFees).toFixed(2))

  return {
    id: `settle_${faker.string.alphanumeric(8)}`,
    settlementUuid: faker.string.uuid(),

    merchantId,
    walletId,

    grossAmount,
    fees,
    netAmount,
    currency,

    status,
    settlementCycle: cycle,

    periodStart: periodStart.toISOString(),
    periodEnd: periodEnd.toISOString(),
    settlementDate: settlementDate.toISOString(),
    expectedDate: expectedDate.toISOString(),
    completedAt: completedAt?.toISOString(),

    transactionCount,
    transactions: transactionIds,

    bankAccount: generateBankAccount(),
    timeline: generateTimeline(status, createdAt, completedAt),

    description: faker.helpers.maybe(() => faker.lorem.sentence(), { probability: 0.3 }),
    reference: `REF-${faker.string.alphanumeric(10).toUpperCase()}`,
    createdAt: createdAt.toISOString(),
    updatedAt: new Date(createdAt.getTime() + faker.number.int({ min: 0, max: 48 }) * 60 * 60 * 1000).toISOString()
  }
}

/**
 * Generate settlements linked to merchants and wallets
 */
export function generateSettlements(
  merchantIds: string[],
  walletsByMerchant: Record<string, Array<{ id: string, currency: CurrencyCode }>>,
  transactionsByWallet: Record<string, string[]>,
  count: number = SETTLEMENT_CONFIG.TOTAL_COUNT
): Settlement[] {
  faker.seed(SETTLEMENT_CONFIG.SEED + 3000)

  const settlements: Settlement[] = []

  for (let i = 0; i < count; i++) {
    // Pick a random merchant
    const merchantId = faker.helpers.arrayElement(merchantIds)

    // Pick a random wallet from that merchant
    const merchantWallets = walletsByMerchant[merchantId] || []
    if (merchantWallets.length === 0) continue

    const wallet = faker.helpers.arrayElement(merchantWallets)

    // Get some transactions for this wallet
    const walletTransactions = transactionsByWallet[wallet.id] || []
    const txCount = faker.number.int({
      min: Math.min(SETTLEMENT_CONFIG.AMOUNT_RANGES.minTransactions, walletTransactions.length),
      max: Math.min(SETTLEMENT_CONFIG.AMOUNT_RANGES.maxTransactions, walletTransactions.length)
    })

    // Sample transactions
    const selectedTx = faker.helpers.arrayElements(
      walletTransactions,
      Math.max(1, txCount)
    )

    settlements.push(generateSettlement(
      merchantId,
      wallet.id,
      wallet.currency,
      selectedTx,
      i
    ))
  }

  // Sort by settlement date (newest first)
  return settlements.sort((a, b) =>
    new Date(b.settlementDate).getTime() - new Date(a.settlementDate).getTime()
  )
}

/**
 * Convert Settlement to SettlementListItem
 */
export function toSettlementListItem(
  settlement: Settlement,
  merchantName: string = 'Unknown Merchant'
): SettlementListItem {
  return {
    id: settlement.id,
    settlementUuid: settlement.settlementUuid,

    merchantId: settlement.merchantId,
    merchantName,
    walletId: settlement.walletId,

    grossAmount: settlement.grossAmount,
    totalFees: settlement.fees.totalFees,
    netAmount: settlement.netAmount,
    currency: settlement.currency,

    status: settlement.status,
    settlementCycle: settlement.settlementCycle,

    settlementDate: settlement.settlementDate,
    expectedDate: settlement.expectedDate,
    completedAt: settlement.completedAt,

    transactionCount: settlement.transactionCount
  }
}

/**
 * Get transaction details for a settlement
 */
export function getSettlementTransactions(
  settlementTransactionIds: string[],
  allTransactions: Array<{
    id: string
    chargeUuid: string
    amount: number
    createdAt: string
    status: string
  }>
): SettlementTransactionRef[] {
  return settlementTransactionIds
    .map((chargeUuid) => {
      const tx = allTransactions.find(t => t.chargeUuid === chargeUuid || t.id === chargeUuid)
      if (!tx) return null

      const fee = Number((tx.amount * 0.025).toFixed(2)) // Approximate 2.5% fee
      return {
        id: tx.id,
        chargeUuid: tx.chargeUuid,
        amount: tx.amount,
        fee,
        netAmount: Number((tx.amount - fee).toFixed(2)),
        createdAt: tx.createdAt,
        status: tx.status
      }
    })
    .filter((t): t is SettlementTransactionRef => t !== null)
}
